import type {
  Category,
  ExpensePaginatedResponse,
  ExpensesListInput,
  ImportExpensesBatchInput,
  Supplier,
  ValidateExpensesImportInput,
  ValidateExpensesImportResponse,
  VehicleExpensesPaginatedResponse,
  VehiclesExpensesInput,
} from '@cobliteam/api-lib/src/__generated__/graphql';
import {
  type CreateExpenseRequest,
  GraphQLTCORequester,
  type ReportExpenseRequest,
  type ReportVehicleExpenseRequest,
  TCORequester,
  type UpdateExpenseRequest,
} from '@cobliteam/api-lib/src/dashboard/totalCostsOfOwnership';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();
const getGraphQLTCORequester = () => new GraphQLTCORequester();
const getTCORequester = () => new TCORequester({ host: env.apiHost });

const TCO_KEY = 'totalCostsOfOwnership';
const TCO_CATEGORIES_KEY = 'categories';
const TCO_SUPPLIERS_KEY = 'suppliers';
const TCO_EXPENSES_KEY = 'expenses';
const TCO_VEHICLES_EXPENSES_KEY = 'vehicles';

const useGetCategories = () => {
  return useQuery<Category[]>({
    queryKey: [TCO_KEY, TCO_CATEGORIES_KEY, 'list'],
    queryFn: async () => await getGraphQLTCORequester().getCategories(),
  });
};

const useGetSuppliers = () => {
  return useQuery<Supplier[]>({
    queryKey: [TCO_KEY, TCO_SUPPLIERS_KEY, 'list'],
    queryFn: async () => await getGraphQLTCORequester().getSuppliers(),
  });
};

const useCreateSupplier = () => {
  return useMutation<Supplier, unknown, { name: string }>({
    mutationKey: [TCO_KEY, TCO_SUPPLIERS_KEY, 'create'],
    mutationFn: (data) => getTCORequester().createSupplier(data.name),
  });
};

const useCreateExpense = () => {
  return useMutation<void, unknown, { createRequest: CreateExpenseRequest }>({
    mutationKey: [TCO_KEY, TCO_EXPENSES_KEY, 'create'],
    mutationFn: (data) => getTCORequester().createExpense(data.createRequest),
  });
};

const useUpdateExpense = () => {
  return useMutation<void, unknown, { id: string; updateRequest: UpdateExpenseRequest }>({
    mutationKey: [TCO_KEY, TCO_EXPENSES_KEY, 'update'],
    mutationFn: (data) => getTCORequester().updateExpense(data.id, data.updateRequest),
  });
};

const useDeleteExpense = () => {
  return useMutation<void, unknown, { id: string }>({
    mutationKey: [TCO_KEY, TCO_EXPENSES_KEY, 'delete'],
    mutationFn: (data) => getTCORequester().deleteExpense(data.id),
  });
};

const useGetExpenses = (input: ExpensesListInput, options?: Partial<UseQueryOptions<ExpensePaginatedResponse>>) => {
  return useQuery({
    queryKey: [TCO_KEY, TCO_EXPENSES_KEY, 'list', input],
    queryFn: async () => await getGraphQLTCORequester().getExpenses(input),
    ...options,
  });
};

const useRequestReport = () => {
  return useMutation<void, unknown, { request: ReportExpenseRequest }>({
    mutationKey: [TCO_KEY, TCO_EXPENSES_KEY, 'report'],
    mutationFn: (data) => getTCORequester().requestReport(data.request),
  });
};

const useRequestVehicleReport = () => {
  return useMutation<void, unknown, { request: ReportVehicleExpenseRequest }>({
    mutationKey: [TCO_KEY, TCO_EXPENSES_KEY, 'vehicle-report'],
    mutationFn: (data) => getTCORequester().requestVehicleReport(data.request),
  });
};

const useExpensesExists = () => {
  return useQuery<boolean, unknown>({
    queryKey: [TCO_KEY, TCO_EXPENSES_KEY, 'exists'],
    queryFn: async () => await getTCORequester().expensesExists(),
  });
};

const useGetVehicleExpenses = (input: Omit<VehiclesExpensesInput, 'timezone'>, options?: Partial<UseQueryOptions<VehicleExpensesPaginatedResponse>>) => {
  return useQuery({
    queryKey: [TCO_KEY, TCO_VEHICLES_EXPENSES_KEY, 'list', input],
    queryFn: async () => await getGraphQLTCORequester().getVehicleExpenses(input),
    ...options,
  });
};

const useValidateExpensesImport = (input: ValidateExpensesImportInput | null) => {
  return useQuery<ValidateExpensesImportResponse | undefined>({
    queryKey: [TCO_KEY, TCO_VEHICLES_EXPENSES_KEY, 'validate-import', input],
    queryFn: async () => {
      if (!input) return undefined;
      return await getGraphQLTCORequester().validateExpensesImport(input);
    },
    enabled: !!input,
  });
};

const useBatchImportExpenses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ImportExpensesBatchInput[]) => await getGraphQLTCORequester().importExpensesBatch(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TCO_KEY, TCO_EXPENSES_KEY],
      });
      Alert.success('Despesas importadas com sucesso!');
    },
    onError: () => Alert.error('Erro ao importar dados. Tente novamente mais tarde.'),
  });
};

export default {
  useGetCategories,
  useGetSuppliers,
  useCreateSupplier,
  useCreateExpense,
  useDeleteExpense,
  useUpdateExpense,
  useGetExpenses,
  useRequestReport,
  useRequestVehicleReport,
  useExpensesExists,
  useGetVehicleExpenses,
  useValidateExpensesImport,
  useBatchImportExpenses,
};
